import { FC, ReactNode, useContext, useRef, useState } from 'react'
import { MdMoreVert, MdPostAdd } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import useOnClickOutside from 'use-onclickoutside'
import { BudgetContext, SidebarContext } from '../../../lib/Context'
import Paywall from '../../Paywall'
import Button from '../../Formik/Button'
import CategoriesForm from '../Configuration/CategoriesForm'
import Configuration from '../Configuration/Configuration'
import Share from '../Configuration/Share'
import * as BudgetAPI from '../lib/API/BudgetAPI'
import { BudgetViewMode } from '../lib/Enums/BudgetViewMode'
import { SheetType } from '../lib/Enums/SheetType'
import { useBudget } from '../lib/Hooks/Budget'
import { useExpenses } from '../lib/Hooks/Spends'
import { isFreeBudgetPremiumLocked } from '../lib/Payment'
import MenuButton from '../MenuButton'
import MenuLink from '../MenuLink'
import PaymentGuard from '../PaymentGuard'
import AddIncome from './AddIncome'
import AddSaving from './AddSaving'
import AddSpend from './AddSpend'
import Allowance from './Allowance/Allowance'
import BalanceOverview from './BalanceOverview'
import BudgetDevelopment from './BudgetDevelopment'
import PremiumFeaturePaywall from './PremiumFeaturePaywall'
import AddReconciliation from './Reconciliation/AddReconciliation'

type Props = {
  viewMode: BudgetViewMode
}

const BudgetMenu: FC<Props> = ({ viewMode }) => {
  const { t } = useTranslation(['budget', 'common'])
  const [isActive, toggleActive] = useState(false)
  const [showAdd, toggleShowAdd] = useState<boolean>(false)
  const [showPremiumPaywall, togglePremiumPaywall] = useState(false)
  const { showSidebar } = useContext(SidebarContext)
  const { setViewMode } = useContext(BudgetContext)
  const { data: budget } = useBudget()
  const { data: spends } = useExpenses(budget!)
  const ref = useRef(null)

  const openSidebar = (content: ReactNode) => {
    showSidebar(content)
    toggleActive(false)
  }

  useOnClickOutside(ref, () => toggleActive(false))

  return (
    <PaymentGuard sheet={budget!}>
      {(guard) => {
        // Premium actions (sharing, allowance, balance overview) get the
        // same 14-day free trial as a paid budget, then lock on a free budget.
        const restrictedGuard = (action: () => void) =>
          guard(() => {
            if (isFreeBudgetPremiumLocked(budget!)) {
              togglePremiumPaywall(true)
              return
            }
            action()
          })

        return (
          <div className='flex flex-wrap md:justify-end mb-4 mt-2 sm:mt-0'>
            {viewMode === BudgetViewMode.EXPENSE && (
              <AddSpend
                toggleHide={() => toggleShowAdd(false)}
                show={showAdd}
              />
            )}
            {viewMode === BudgetViewMode.INCOME && (
              <AddIncome
                toggleHide={() => toggleShowAdd(false)}
                show={showAdd}
              />
            )}
            {viewMode === BudgetViewMode.SAVING && (
              <AddSaving
                toggleHide={() => toggleShowAdd(false)}
                show={showAdd}
              />
            )}
            {viewMode === BudgetViewMode.RECONCILIATION && (
              <AddReconciliation
                toggleHide={() => toggleShowAdd(false)}
                show={showAdd}
              />
            )}
            <div className='relative' ref={ref}>
              <Button
                theme='gray'
                className={`md:ml-2 cursor-pointer ${isActive ? 'rounded-b-none' : ''}`}
                onClick={() => toggleActive(!isActive)}
              >
                <MdMoreVert size='1.5rem' />
              </Button>
              {isActive && (
                <div className='absolute top-10 left-2 bg-white border-gray-300 border-2 px-4 pt-4 z-10'>
                  <div className='sm:hidden'>
                    <MenuLink
                      title={t('budget:budgetMenu.share')}
                      icon='MdShare'
                      action={restrictedGuard(() => openSidebar(<Share />))}
                    />
                  </div>
                  {viewMode === BudgetViewMode.EXPENSE && (
                    <div className='md:hidden'>
                      <MenuLink
                        title={t('budget:budgetMenu.categories')}
                        icon='MdCategory'
                        action={guard(() =>
                          openSidebar(<CategoriesForm sheet={budget!} />),
                        )}
                      />
                    </div>
                  )}
                  <MenuLink
                    title={t('budget:budgetMenu.allowance')}
                    icon='MdAccountBalanceWallet'
                    action={restrictedGuard(() =>
                      openSidebar(
                        <Allowance items={spends!} setViewMode={setViewMode} />,
                      ),
                    )}
                  />
                  <MenuLink
                    title={t('budget:shared.development')}
                    icon='MdAutoGraph'
                    action={guard(() =>
                      openSidebar(<BudgetDevelopment budget={budget!} />),
                    )}
                  />
                  <MenuLink
                    title={t('budget:budgetMenu.balanceOverview')}
                    icon='MdInsertChart'
                    action={restrictedGuard(() =>
                      openSidebar(<BalanceOverview spends={spends!} />),
                    )}
                  />
                </div>
              )}
            </div>
            <div className='hidden sm:block'>
              <MenuButton
                sheet={budget!}
                title={t('budget:budgetMenu.share')}
                icon='MdShare'
                onClick={() => showSidebar(<Share />)}
                locked={isFreeBudgetPremiumLocked(budget!)}
              >
                {isFreeBudgetPremiumLocked(budget!)
                  ? <PremiumFeaturePaywall />
                  : undefined}
              </MenuButton>
            </div>
            {viewMode === BudgetViewMode.EXPENSE && (
              <div className='hidden md:block'>
                <MenuButton
                  sheet={budget!}
                  title={t('budget:budgetMenu.categories')}
                  icon='MdCategory'
                  onClick={() => showSidebar(<CategoriesForm sheet={budget!} />)}
                />
              </div>
            )}
            <MenuButton
              sheet={budget!}
              icon='MdDownload'
              onClick={() => BudgetAPI.downloadBudget(budget!)}
            />
            <MenuButton
              sheet={budget!}
              icon='MdSettings'
              onClick={() => showSidebar(<Configuration sheet={budget!} />)}
            />
            <Button onClick={guard(() => toggleShowAdd(true))} className='ml-2'>
              <div className='flex items-center'>
                <MdPostAdd size='1.25rem' className='mr-2' />
                {viewMode === BudgetViewMode.RECONCILIATION
                  ? t('budget:budgetMenu.addPayment')
                  : t('budget:shared.add')}
              </div>
            </Button>
            <Paywall
              show={showPremiumPaywall}
              onHide={() => togglePremiumPaywall(false)}
              sheetType={SheetType.BUDGET}
              sheetId={budget!.id}
              name={budget!.name || t('common:paywall.budget')}
            >
              <PremiumFeaturePaywall />
            </Paywall>
          </div>
        )
      }}
    </PaymentGuard>
  )
}

export default BudgetMenu
